import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { langNameJa, speechBcp47 } from '../data/languages';
import PostText from './PostText';
import CommentsSheet from './CommentsSheet';
import CommentMentionInput from './CommentMentionInput';
import { IconHeart, IconVolume, IconBubble } from './talkAppleIcons';
import ProfileOverlayModal from './ProfileOverlayModal';
import ReportModal from './ReportModal';
import { PersonAvatar } from './PeopleUI';

export default function PostCard({ post, onLike, todayTopicId, onHidden, showTalkButton = true, commentsAsSheet = false, feedLayout = false }) {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const [liked, setLikedAnim]           = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments]         = useState([]);
  const [loadedComments, setLoadedComments] = useState(false);
  const [commentText, setCommentText]   = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [commentError, setCommentError] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const commentSending = useRef(false);
  const commentInputRef = useRef(null);
  const likingComments = useRef(new Set());
  const [likeBusyIds, setLikeBusyIds] = useState(new Set());
  const [commentCount, setCommentCount] = useState(Number(post.comment_count) || 0);
  const [showLikers, setShowLikers]     = useState(false);
  const [likers, setLikers]             = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { type, id, name }
  const [blockBusy, setBlockBusy]       = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState('');
  const [pendingHide, setPendingHide]   = useState(null); // { targetType, targetId }

  const isMine = post.user_id === user?.id;

  // Guideline 1.2: 通報したコンテンツは画面から消す。
  // ただし「24時間以内に対応します」の完了画面を読んでもらうため、
  // 非表示にするのはモーダルを閉じたタイミングにする。
  const closeReport = () => {
    setReportTarget(null);
    if (!pendingHide) return;
    const { targetType, targetId } = pendingHide;
    setPendingHide(null);
    if (targetType === 'post') onHidden?.(post.id);
    if (targetType === 'comment') setComments(cs => cs.filter(c => String(c.id) !== targetId));
  };

  const blockAuthor = async () => {
    setMenuOpen(false);
    if (!window.confirm(`${post.username} さんをブロックしますか？\nこの人の投稿・コメント・メッセージは表示されなくなります。`)) return;
    setBlockBusy(true);
    try {
      await axios.post(`/api/users/${post.user_id}/block`);
      onHidden?.(post.id);
    } catch (_) {
      window.alert('ブロックに失敗しました。時間をおいて再度お試しください。');
    } finally {
      setBlockBusy(false);
    }
  };

  const speak = text => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = speechBcp47(post.language);
    u.rate = 0.9;
    window.speechSynthesis.speak(u);
  };

  const toggleLike = async () => {
    setLikedAnim(true);
    setTimeout(() => setLikedAnim(false), 400);
    const { data } = await axios.post(`/api/posts/${post.id}/like`);
    onLike?.(post.id, data.liked);
  };

  const loadComments = async () => {
    if (!loadedComments) {
      setComments((await axios.get(`/api/posts/${post.id}/comments`)).data);
      setLoadedComments(true);
    }
    setShowComments(v => !v);
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text || commentSending.current) return;
    commentSending.current = true; setCommentBusy(true); setCommentError('');
    try {
      const { data } = await axios.post(`/api/posts/${post.id}/comments`, { text, ...(mentionUsers.length ? { mention_ids: mentionUsers.map(person => person.id) } : {}), ...(replyTo ? { parent_id: replyTo.id } : {}) });
      setComments(c => [...c, data]); setCommentCount(n => n + 1);
      setCommentText(''); setReplyTo(null); setMentionUsers([]);
    } catch { setCommentError('送信できませんでした。もう一度お試しください。'); }
    finally { commentSending.current = false; setCommentBusy(false); }
  };

  const likeComment = async comment => {
    if (likingComments.current.has(comment.id)) return;
    likingComments.current.add(comment.id); setLikeBusyIds(new Set(likingComments.current)); setCommentError('');
    try {
      const { data } = await axios.post(`/api/posts/${post.id}/comments/${comment.id}/like`);
      setComments(items => items.map(item => item.id === comment.id ? { ...item, user_liked: data.liked, like_count: data.like_count } : item));
    } catch { setCommentError('いいねを変更できませんでした。もう一度お試しください。'); }
    finally { likingComments.current.delete(comment.id); setLikeBusyIds(new Set(likingComments.current)); }
  };

  const openPartnerChat = async (e, partnerId) => {
    e.stopPropagation();
    if (chatBusy) return;
    setChatBusy(true); setChatError('');
    try {
      await axios.post('/api/contacts', { partner_id: partnerId });
      navigate(`/chat/${partnerId}`, { state: { postContext: {
        id: post.id, user_id: post.user_id, username: post.username,
        content: Array.from(post.content || '').slice(0, 160).join(''),
        hasImage: !!post.image_url, hasAudio: !!post.audio_url,
      } } });
    } catch {
      setChatError('チャットを開けませんでした。もう一度お試しください。');
    } finally { setChatBusy(false); }
  };

  const openLikers = async () => {
    if (likers === null) {
      const { data } = await axios.get(`/api/posts/${post.id}/likers`);
      setLikers(data);
    }
    setShowLikers(v => !v);
  };

  const timeAgo = dateStr => {
    const d = (Date.now() - new Date(dateStr)) / 1000;
    if (d < 60)    return '今';
    if (d < 3600)  return `${Math.floor(d / 60)}分前`;
    if (d < 86400) return `${Math.floor(d / 3600)}時間前`;
    return `${Math.floor(d / 86400)}日前`;
  };

  const isTodayTopic = todayTopicId && Number(post.topic_id) === todayTopicId;

  const caption = (post.content || '').trim() ? (
    <p className="jt-post-body" data-post-part="caption" style={S.caption}>
      {feedLayout && <button className="jt-feed-caption-author" type="button" onClick={() => setProfileUserId(post.user_id)}>{post.username}</button>}
      <PostText content={post.content} />
    </p>
  ) : null;
  const captionBelow = feedLayout && !!(post.image_url || post.audio_url);

  const renderCommentText = comment => {
    const parts = []; let cursor = 0;
    for (const mention of comment.mentions || []) {
      if (mention.start < cursor || mention.end > comment.text.length) continue;
      parts.push(comment.text.slice(cursor, mention.start));
      parts.push(<button className="jt-comment-mention" type="button" key={mention.start} onClick={() => { setShowComments(false); setProfileUserId(mention.user_id); }}>{comment.text.slice(mention.start, mention.end)}</button>);
      cursor = mention.end;
    }
    parts.push(comment.text.slice(cursor)); return parts;
  };

  const commentsPanel = (
        <div data-post-part="commentPanel" style={S.commentPanel}>
          {comments.length === 0 && (
            <p data-post-part="noCorr" style={S.noCorr}>まだコメントがありません</p>
          )}
          {comments.map(c => (
            <div key={c.id} className="jt-comment" data-post-part="commentItem" style={S.commentItem}>
              <button className="jt-comment-avatar" type="button" aria-label={`${c.username}のプロフィール`} onClick={() => { setShowComments(false); setProfileUserId(c.user_id); }}>
                <PersonAvatar person={c} size={36} />
              </button>
              <div className="jt-comment-copy">
                <button className="jt-comment-author" type="button" onClick={() => { setShowComments(false); setProfileUserId(c.user_id); }}>
                  {c.username}{c.user_id === user?.id && <span className="jt-comment-self">（あなた）</span>}
                </button>
                {c.parent_id && <p className="jt-comment-reply-label">{c.parent_username ? `${c.parent_username}さんへの返信` : 'コメントへの返信'}</p>}
                <p className="jt-comment-text">{renderCommentText(c)}</p>
                <div className="jt-comment-actions">
                  <button type="button" disabled={commentBusy} onClick={() => { setReplyTo(c); setCommentError(''); commentInputRef.current?.focus(); }}>返信する</button>
                  <button type="button" aria-label={`${c.username} さんのコメントにいいね`} aria-pressed={!!c.user_liked} disabled={likeBusyIds.has(c.id)} onClick={() => likeComment(c)}>
                    <IconHeart size={16} filled={!!c.user_liked} /><span>{c.like_count || 0}</span>
                  </button>
                </div>
              </div>
              {c.user_id !== user?.id && (
                <details className="jt-comment-menu">
                  <summary aria-label={`${c.username} さんのコメントの操作`}>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
                  </summary>
                  <button data-control="quiet" data-control-id="PostCard-commentReport" type="button"
                    onClick={() => { setShowComments(false); setReportTarget({ type: 'comment', id: c.id, name: `${c.username} さんのコメント` }); }}
                    aria-label={`${c.username} さんのコメントを通報`}>通報する</button>
                </details>
              )}
            </div>
          ))}
          {commentError && <p className="jt-comment-error" role="alert">{commentError}</p>}
          <div className="jt-comment-compose">
          {replyTo && <div className="jt-comment-reply-target"><span>{replyTo.username}さんに返信</span><button type="button" disabled={commentBusy} onClick={() => setReplyTo(null)} aria-label="返信をキャンセル">×</button></div>}
          <div data-post-part="commentInputRow" style={S.commentInputRow}>
            <CommentMentionInput inputRef={commentInputRef} value={commentText} onChange={setCommentText} disabled={commentBusy} style={S.commentInput} onSubmit={submitComment} onMention={person => setMentionUsers(items => [...items.filter(item => item.id !== person.id), person])} />
            <button data-control="primary" data-control-id="PostCard-csend" data-post-part="csend" style={{ ...S.csend, flexShrink: 0, ...(commentText.trim() ? {} : S.csendDisabled) }} onClick={submitComment} disabled={commentBusy || !commentText.trim()}>{commentBusy ? '送信中…' : commentsAsSheet ? '送信' : '返信する'}</button>
          </div>
          </div>
        </div>
  );

  return (
    <article className={`post-article${feedLayout ? ' jt-feed-post' : ''}`} data-post-part="post" style={S.post}>

      {/* ── Header ── */}
      <div data-post-part="postHeader" style={S.postHeader}>
        <button data-control="media" data-control-id="PostCard-avatarWrap"
          type="button"
          data-post-part="avatarWrap" style={S.avatarWrap}
          aria-label={`${post.username}のプロフィール`}
          onClick={() => setProfileUserId(post.user_id)}
        >
          <PersonAvatar person={post} size={38} />
        </button>
        <div data-post-part="headerInfo" style={S.headerInfo}>
          <button data-control="text" data-control-id="PostCard-username" type="button" data-post-part="username" style={S.username} onClick={() => setProfileUserId(post.user_id)}>
            {post.username}
          </button>
          <span data-post-part="langMeta" style={S.langMeta}>
            {langNameJa(post.native_lang)} → {langNameJa(post.learning_lang)}
          </span>
        </div>
        <div data-post-part="headerAside" style={S.headerAside}>
          {!feedLayout && <span data-post-part="headerTime" style={S.headerTime}>{timeAgo(post.created_at)}</span>}
          {isTodayTopic && <span data-post-part="topicChip" style={S.topicChip}>今日の話題</span>}
        </div>

        {/* ── 通報・ブロックメニュー（Guideline 1.2） ── */}
        {!isMine && (
          <div data-post-part="menuWrap" style={S.menuWrap}>
            <button data-control="icon" data-control-id="PostCard-moreBtn"
              data-post-part="moreBtn" style={S.moreBtn}
              onClick={() => setMenuOpen(v => !v)}
              aria-label="この投稿の操作"
              aria-expanded={menuOpen}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <circle cx="5" cy="12" r="1.9" />
                <circle cx="12" cy="12" r="1.9" />
                <circle cx="19" cy="12" r="1.9" />
              </svg>
            </button>
            {menuOpen && (
              <>
                <div data-post-part="menuBackdrop" style={S.menuBackdrop} onClick={() => setMenuOpen(false)} />
                <div data-post-part="menu" style={S.menu} role="menu">
                  <button data-control="row" data-control-id="PostCard-menuItem"
                    data-post-part="menuItem" style={S.menuItem}
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setReportTarget({ type: 'post', id: post.id, name: `${post.username} さんの投稿` });
                    }}
                  >
                    この投稿を通報する
                  </button>
                  <button data-control="row" data-control-id="PostCard-menuItem" data-post-part="menuItem" style={S.menuItem} role="menuitem" onClick={blockAuthor} disabled={blockBusy}>
                    {blockBusy ? '処理中…' : `${post.username} をブロック`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {!captionBelow && caption}
      {post.audio_url && (
        <div data-post-part="audioWrap" style={S.audioWrap}>
          <audio controls src={post.audio_url} data-post-part="audio" style={S.audio} />
        </div>
      )}

      {/* ── Image ── */}
      {post.image_url && (
        <img src={post.image_url} alt="" data-post-part="postImage" style={S.postImage} loading="lazy" />
      )}

      {/* ── Action bar ── */}
      <div data-post-part="actions" style={S.actions}>
        <div data-post-part="actionsLeft" style={S.actionsLeft}>
          <button data-control="icon" data-control-id="PostCard-iconBtn"
            data-post-part="iconBtn" style={{ ...S.iconBtn, ...(post.user_liked ? S.iconBtnLiked : {}), ...(liked ? S.iconBtnPop : {}) }}
            onClick={toggleLike} aria-label="いいね" aria-pressed={!!post.user_liked}
          >
            <IconHeart size={24} filled={!!post.user_liked} />
          </button>
          <button data-control="icon" data-control-id="PostCard-iconBtn"
            data-post-part="iconBtn" style={{ ...S.iconBtn, ...(showComments ? S.iconBtnActive : {}) }}
            onClick={loadComments} aria-label="コメント" aria-expanded={showComments}
          >
            <IconBubble size={22} />
          </button>
          <button data-control="icon" data-control-id="PostCard-iconBtn"
            data-post-part="iconBtn" style={{ ...S.iconBtn, ...(!(post.content || '').trim() ? S.iconBtnDisabled : {}) }}
            onClick={() => speak(post.content)} aria-label="読み上げ"
            disabled={!(post.content || '').trim()}
          >
            <IconVolume size={22} />
          </button>
        </div>
        <div data-post-part="actionsRight" style={S.actionsRight}>
          {showTalkButton && post.user_id !== user?.id && (
            <button data-control="quiet" data-control-id="PostCard-talkBtn" data-post-part="talkBtn" style={S.talkBtn} disabled={chatBusy} onClick={e => openPartnerChat(e, post.user_id)}>
              <IconBubble size={20} />
              <span data-post-part="talkBtnLabel" style={S.talkBtnLabel}>{chatBusy ? '開いています…' : '投稿の話をする'}</span>
            </button>
          )}
          
        </div>
      </div>

      {chatError && <p role="alert" style={{ padding: '8px 16px', fontSize: 12, lineHeight: '20px', color: 'var(--text)' }}>{chatError}</p>}

      {/* ── Like count (tappable → likers) ── */}
      {post.like_count > 0 && (
        <button data-control="text" data-control-id="PostCard-likeCountBtn" data-post-part="likeCountBtn" style={S.likeCountBtn} onClick={openLikers}>
          <strong>{post.like_count}</strong>人がいいね
        </button>
      )}

      {/* ── Likers panel ── */}
      {showLikers && (
        <div data-post-part="likersPanel" style={S.likersPanel}>
          {likers === null && <p data-post-part="noCorr" style={S.noCorr}>読み込み中…</p>}
          {likers?.length === 0 && <p data-post-part="noCorr" style={S.noCorr}>まだいいねがありません</p>}
          {likers?.map(u => (
            <button data-control="row" data-control-id="PostCard-likerRow" key={u.id} data-post-part="likerRow" style={S.likerRow} onClick={() => { setShowLikers(false); setProfileUserId(u.id); }}>
              <div data-post-part="likerAvatar" style={S.likerAvatar}>
                {u.avatar
                  ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span data-post-part="likerAvatarTxt" style={S.likerAvatarTxt}>{u.username[0].toUpperCase()}</span>
                }
              </div>
              <span data-post-part="likerName" style={S.likerName}>{u.username}</span>
            </button>
          ))}
        </div>
      )}

      {captionBelow && caption}

      {/* ── Comment count ── */}
      {commentCount > 0 && (
        <div data-post-part="countRow" style={S.countRow}>
          <button data-control="text" data-control-id="PostCard-countLink" data-post-part="countLink" style={S.countLink} onClick={loadComments}>
            コメント {commentCount}件
          </button>
        </div>
      )}

      {feedLayout && <time className="jt-feed-time" dateTime={post.created_at}>{timeAgo(post.created_at)}</time>}

      {showComments && (commentsAsSheet
        ? <CommentsSheet onClose={() => setShowComments(false)}>{commentsPanel}</CommentsSheet>
        : commentsPanel)}

      {/* ── 通報モーダル ── */}
      {reportTarget && (
        <ReportModal
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetName={reportTarget.name}
          onClose={closeReport}
          onReported={setPendingHide}
        />
      )}

      {/* ── Profile overlay modal ── */}
      {profileUserId && (
        <ProfileOverlayModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
          onNavigate={id => { setProfileUserId(null); navigate(`/profile/${id}`); }}
        />
      )}

    </article>
  );
}

const S = {
  post: {
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border-light)',
  },

  postHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '16px 16px 12px',
  },
  avatarWrap: {
    width: 44, height: 44, borderRadius: '50%', padding: 0, border: 0,
    flexShrink: 0, cursor: 'pointer', background: 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  headerInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 },
  username: { padding: 0, border: 0, background: 'transparent', textAlign: 'left', fontFamily: 'inherit', fontWeight: 600, fontSize: 14, color: 'var(--text)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  langMeta: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  categoryBadge: {
    fontSize: 10, fontWeight: 700, color: 'var(--primary)',
    background: 'var(--primary-light)', borderRadius: 6, padding: '1px 6px',
  },
  headerAside: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 },
  headerTime: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, flexShrink: 0 },

  menuWrap: { position: 'relative', flexShrink: 0 },
  moreBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, marginRight: -4,
    background: 'none', border: 'none', borderRadius: '50%',
    color: 'var(--text-muted)', cursor: 'pointer', padding: 0,
  },
  menuBackdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  menu: {
    position: 'absolute', top: 32, right: 0, zIndex: 41,
    minWidth: 208, padding: 6,
    background: 'var(--surface)',
    border: '1px solid var(--border-light)',
    borderRadius: 'var(--r-card)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
    display: 'flex', flexDirection: 'column', gap: 2,
    animation: 'fadeInUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
  },
  menuItem: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '10px 12px', borderRadius: 10,
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
    color: 'var(--text)', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis',
  },

  postImage: {
    width: '100%', display: 'block',
    objectFit: 'cover', maxHeight: 480,
    background: 'var(--border-light)',
  },

  actions: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0', margin: '0 16px', borderTop: '1px solid var(--border-light)',
  },
  actionsLeft: { display: 'flex', alignItems: 'center' },
  actionsRight: { display: 'flex', alignItems: 'center', gap: 8, paddingRight: 6 },
  iconBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '8px', minWidth: 44, minHeight: 44, borderRadius: 8,
    display: 'flex', alignItems: 'center',
    color: 'var(--text)',
    transition: 'transform 0.15s',
    userSelect: 'none',
  },
  iconBtnLiked: { color: '#E11D48' },
  iconBtnActive: { color: 'var(--primary)' },
  iconBtnPop: { animation: 'heartPop 0.45s cubic-bezier(0.34,1.56,0.64,1)' },
  iconBtnDisabled: { opacity: 0.3, cursor: 'default' },
  talkBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'none', border: '1px solid var(--border)',
    borderRadius: 6, padding: '8px 10px', minHeight: 44,
    color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'border-color 0.15s, color 0.15s',
  },
  talkBtnLabel: { fontSize: 12, fontWeight: 600 },
  topicChip: {
    fontSize: 10, fontWeight: 400, color: 'var(--text-secondary)',
    background: 'var(--bg)', padding: '3px 6px', borderRadius: 4,
  },

  likeCountBtn: {
    display: 'block', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '0 16px 12px', fontFamily: 'inherit',
    fontSize: 13, color: 'var(--text)',
  },

  likersPanel: {
    background: 'var(--bg)',
    borderTop: '1px solid var(--border-light)',
    padding: '8px 12px',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  likerRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '5px 0', textAlign: 'left', width: '100%',
    fontFamily: 'inherit',
  },
  likerAvatar: {
    width: 32, height: 32, borderRadius: '50%',
    background: 'var(--primary)', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  likerAvatarTxt: { color: '#fff', fontWeight: 700, fontSize: 13 },
  likerName: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },

  caption: {
    fontSize: 15, lineHeight: 1.85, color: 'var(--text)',
    margin: 0, padding: '0 16px 18px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
  },

  audioWrap: { padding: '0 12px 10px' },
  audio: { width: '100%', height: 36 },

  countRow: { display: 'flex', gap: 12, padding: '0 16px 12px', flexWrap: 'wrap' },
  countLink: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 0, fontFamily: 'inherit',
    fontSize: 13, color: 'var(--text-muted)', fontWeight: 400,
  },

  noCorr: { fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '6px 0', margin: 0 },

  commentPanel: {
    background: 'var(--bg)',
    borderTop: '1px solid var(--border-light)',
    padding: '12px 16px',
  },
  commentItem: { display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' },
  commentUser: { fontWeight: 700, fontSize: 13, color: 'var(--text)', flexShrink: 0 },
  commentText: { fontSize: 13, color: 'var(--text)', lineHeight: 1.5 },
  commentReport: {
    marginLeft: 'auto', flexShrink: 0,
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '0 0 0 8px', fontFamily: 'inherit',
    fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
  },
  commentInputRow: {
    display: 'flex', gap: 8, alignItems: 'center',
    marginTop: 10, borderTop: '1px solid var(--border-light)', paddingTop: 10,
  },
  commentInput: {
    flex: 1, padding: '9px 12px', borderRadius: 'var(--r-input)',
    border: '1px solid var(--border)',
    fontSize: 14, fontFamily: 'inherit',
    background: 'var(--surface)', outline: 'none', color: 'var(--text)',
    minWidth: 0,
  },
  csend: {
    background: 'var(--primary)', color: '#fff',
    border: 'none', borderRadius: 20,
    padding: '9px 20px', fontWeight: 700,
    fontFamily: 'inherit', fontSize: 14,
    cursor: 'pointer', alignSelf: 'flex-end',
  },
  csendDisabled: { opacity: 0.4, cursor: 'default' },
};
